// @ts-check
// Vínculos de notas e tarefas a qualquer registro via noteTarget / taskTarget (doc 10, 6.2).
import { objectsByNameSingular } from '../../data/metadata-store.js';
import { createRecord } from '../../data/record-store.js';
import { findManyRecords } from '../../data/rest.js';
import { getRelationIdFieldName, isManyToOneRelation, toPascalCase } from '../../lib/field-metadata.js';

/**
 * @param {'task' | 'note'} activityNameSingular
 * @param {string} targetObjectNameSingular
 */
export function getActivityTargetConfig(activityNameSingular, targetObjectNameSingular) {
  const activityObject = objectsByNameSingular.value.get(activityNameSingular);
  const junctionObject = objectsByNameSingular.value.get(`${activityNameSingular}Target`);
  if (!activityObject || !junctionObject) return null;
  const junctionFields = junctionObject.fields ?? [];
  const activityField = junctionFields.find(
    (field) => isManyToOneRelation(field) && field.relation?.targetObjectNameSingular === activityNameSingular,
  );
  const targetField = junctionFields.find(
    (field) => isManyToOneRelation(field) && field.relation?.targetObjectNameSingular === targetObjectNameSingular,
  );
  return {
    activityObject,
    junctionObject,
    activityIdFieldName: activityField ? getRelationIdFieldName(activityField) : `${activityNameSingular}Id`,
    targetIdFieldName: targetField ? getRelationIdFieldName(targetField) : `target${toPascalCase(targetObjectNameSingular)}Id`,
  };
}

/**
 * Busca as atividades ligadas a um registro em dois passos: junções e depois as atividades.
 * @param {'task' | 'note'} activityNameSingular
 * @param {string} targetObjectNameSingular
 * @param {string} targetRecordId
 */
export async function findActivitiesForRecord(activityNameSingular, targetObjectNameSingular, targetRecordId) {
  const config = getActivityTargetConfig(activityNameSingular, targetObjectNameSingular);
  if (!config) return { config: null, activities: [] };
  const junctions = await findManyRecords(config.junctionObject, {
    filter: { field: config.targetIdFieldName, operator: 'eq', value: targetRecordId },
    limit: 200,
    depth: 0,
  });
  const activityIds = [...new Set(junctions.records.map((junction) => junction[config.activityIdFieldName]).filter(Boolean))];
  if (activityIds.length === 0) return { config, activities: [] };
  const activities = await findManyRecords(config.activityObject, {
    filter: { field: 'id', operator: 'in', value: activityIds },
    orderBy: [{ field: 'createdAt', direction: 'DescNullsFirst' }],
    limit: 200,
    depth: 0,
  });
  return { config, activities: activities.records };
}

/**
 * Cria a atividade e a junção com o registro alvo.
 * @param {'task' | 'note'} activityNameSingular
 * @param {string} targetObjectNameSingular
 * @param {string} targetRecordId
 * @param {Record<string, unknown>} data
 */
export async function createActivityForRecord(activityNameSingular, targetObjectNameSingular, targetRecordId, data) {
  const config = getActivityTargetConfig(activityNameSingular, targetObjectNameSingular);
  if (!config) throw new Error(`Missing ${activityNameSingular}Target metadata`);
  const activity = await createRecord(config.activityObject, data);
  await createRecord(config.junctionObject, {
    [config.activityIdFieldName]: activity.id,
    [config.targetIdFieldName]: targetRecordId,
  });
  return activity;
}

/**
 * Corpo em texto simples com markdown leve (contrato MVP, seção 1), no campo RICH_TEXT do Twenty.
 * @param {import('../../lib/field-metadata.js').ObjectMetadata} activityObject
 */
export function getActivityBodyField(activityObject) {
  const fields = activityObject.fields ?? [];
  return (
    fields.find((field) => field.name === 'bodyV2') ??
    fields.find((field) => field.type === 'RICH_TEXT') ??
    fields.find((field) => field.name === 'body')
  );
}

/**
 * @param {any} value
 */
export function getBodyText(value) {
  if (!value) return '';
  if (typeof value === 'string') return value;
  return value.markdown ?? '';
}
